import { z } from 'zod'

/**
 * Connection type enum schema
 */
export const connectionTypeSchema = z.enum(['oauth2', 'basic', 'apiKey', 'keyPair', 'database'])

/**
 * Response schema for integration connection details (ResourceRecord from database)
 */
export const integrationConnectionDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  active: z.boolean().nullable().optional(),
  provider: z.string().nullable().optional(),
  workspaceId: z.string(),
  config: z.unknown().optional(),
  appId: z.string().nullable().optional(),
  connectionId: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.date().nullable().optional(),
  updatedAt: z.date().nullable().optional(),
})

/**
 * Response schema for integration connections
 */
export const integrationConnectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: connectionTypeSchema,
  isConnected: z.boolean(),
  connectionDetails: z.array(integrationConnectionDetailsSchema),
})

/**
 * Schema for integration action
 */
export const integrationActionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  available: z.boolean().optional(),
})

/**
 * Response schema for individual integration
 */
export const integrationResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  logoUrl: z.string(),
  isPublished: z.boolean(),
  availableForAgent: z.boolean(),
  isLlmModel: z.boolean(),
  models: z.array(z.string()).optional(),
  connections: z.array(integrationConnectionSchema),
  actions: z.array(integrationActionSchema).optional(),
})

/**
 * API response schema for integrations list
 */
export const integrationsListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(integrationResponseSchema),
  message: z.string(),
})

/**
 * Error response schema
 */
export const integrationErrorResponseSchema = z.object({
  success: z.boolean(),
  error: z.object({
    type: z.string(),
    message: z.string(),
    details: z.string().optional(),
  }),
})

/**
 * Integration form data schema
 *
 * Requires appId and connectionId to ensure proper integration
 * app matching and logo display in the connection list.
 */
export const saveIntegrationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  provider: z.string().min(1, 'Provider is required'),
  appId: z.string().min(1, 'App ID is required for proper integration'),
  connectionId: z.string().min(1, 'Connection ID is required for proper integration'),
  apiKey: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  publicKey: z.string().optional(),
  privateKey: z.string().optional(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  redirectUri: z.string().optional(),
  scopes: z.string().optional(),
  baseUrl: z.string().optional(),
  config: z.record(z.string(), z.any()).optional(),
})

// Types for TypeScript
export type ConnectionType = z.infer<typeof connectionTypeSchema>
export type IntegrationConnectionDetails = z.infer<typeof integrationConnectionDetailsSchema>
export type IntegrationConnection = z.infer<typeof integrationConnectionSchema>
export type IntegrationAction = z.infer<typeof integrationActionSchema>
export type IntegrationResponse = z.infer<typeof integrationResponseSchema>
export type IntegrationsListResponse = z.infer<typeof integrationsListResponseSchema>
export type IntegrationErrorResponse = z.infer<typeof integrationErrorResponseSchema>
export type SaveIntegrationData = z.infer<typeof saveIntegrationSchema>